import { useState } from 'react';

// json import removed - return plain objects in React Router v7
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';

import Header from '~/components/Header';
import TryForFreeButton from '~/components/TryForFreeButton';
import { createLogger } from '~/utils/logger.server';

export const handle = {
  title: 'How to Speak Confidently in Job Interviews | Pavone Academy',
  description: 'Master job interview communication with expert tips on answering questions confidently, handling interview anxiety, and making a strong impression on hiring managers.',
};

const logger = createLogger('AcademyJobInterviewArticle');

export async function loader(args: LoaderFunctionArgs) {
  await logger.info('Academy job interview article loaded');
  return Response.json({});
}

export const meta: MetaFunction = () => {
  return [
    { title: 'How to Speak Confidently in Job Interviews - Pavone' },
    { 
      name: 'description', 
      content: 'Learn proven strategies to speak confidently in job interviews, answer tough questions, reduce interview anxiety, and impress hiring managers. Expert interview preparation tips.' 
    },
    { name: 'keywords', content: 'job interview confidence, how to speak in interviews, interview communication skills, answering interview questions confidently, job interview tips, reduce interview anxiety, interview preparation' },
    { property: 'og:title', content: 'How to Speak Confidently in Job Interviews' },
    { property: 'og:description', content: 'Master job interview communication with expert tips on speaking confidently and making a strong impression.' },
    { property: 'og:type', content: 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'How to Speak Confidently in Job Interviews' },
    { name: 'twitter:description', content: 'Learn proven strategies to speak confidently in job interviews and impress hiring managers.' },
  ];
};

export default function JobInterviewConfidenceArticle() {
  const [isShared, setIsShared] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    const title = 'How to Speak Confidently in Job Interviews';
    const text = 'Check out this article on mastering job interview communication:';

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url,
        });
        setIsShared(true);
        toast.success('Article shared!');
        setTimeout(() => setIsShared(false), 2000);
      } else {
        await navigator.clipboard.writeText(url);
        setIsShared(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setIsShared(false), 2000);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setIsShared(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setIsShared(false), 2000);
        } catch (clipboardError) {
          toast.error('Failed to share. Please copy the link manually.');
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <article className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 text-brand-dark-blue hover:text-brand-gold transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Academy</span>
          </Link>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 rounded-2xl overflow-hidden"
          >
            <img
              src="/images/academy/online_interview.jpg"
              alt="Job interview confidence"
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
            />
          </motion.div>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full">
                Career Success
              </span>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>9 min read</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-brand-dark-blue mb-6 leading-tight">
              How to Speak Confidently in Job Interviews
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              The insider's guide to mastering interview communication, answering tough questions with confidence, 
              and leaving hiring managers impressed — even when you're nervous.
            </p>

            <div className="flex items-center justify-between py-4 border-y border-gray-200 mb-12">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Published:</span> November 3, 2024
              </div>
              <button
                onClick={handleShare}
                className={`flex items-center gap-2 text-sm transition-colors ${
                  isShared
                    ? 'text-brand-green'
                    : 'text-gray-600 hover:text-brand-dark-blue'
                }`}
              >
                {isShared ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4" />
                    <span>Share</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I'll never forget bombing my first real job interview. I walked in prepared, resume polished, suit pressed. 
              But the moment the hiring manager asked "Tell me about yourself," my mind went blank. I stammered through 
              some disconnected facts about my education and experience, and I could see their interest fade in real time. 
              I didn't get the job.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              That failure taught me something crucial: <strong>job interviews aren't just about what you know — they're 
              about how you communicate what you know.</strong> You can be the most qualified candidate in the room, but 
              if you can't articulate your value confidently, you won't get the offer. The good news? Interview confidence 
              is a skill you can learn.
            </p>

            {/* Section 1 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Why Job Interviews Feel So Intimidating
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Job interviews activate every insecurity we have. You're being evaluated. Judged. Compared to other 
              candidates. Your livelihood depends on impressing someone you just met. No wonder your palms sweat and 
              your voice shakes.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              But here's what helped me reframe interview anxiety: <strong>interviews aren't interrogations. They're 
              conversations where both parties are evaluating fit.</strong> The hiring manager isn't trying to catch you 
              out — they're hoping you're the solution to their problem. They want you to succeed.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              When you shift from "I need to impress them" to "I need to show them how I can help," something changes. 
              The pressure eases. Because now it's not about being perfect — it's about being authentic and useful.
            </p>

            {/* Section 2 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Preparation: The Foundation of Interview Confidence
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I've coached dozens of people through job interviews, and the ones who speak most confidently aren't 
              necessarily the most experienced. They're the most prepared.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              One client, a software developer switching careers, was terrified of interviews. We spent three weeks 
              preparing answers to common questions. Not memorizing scripts — developing <strong>flexible talking points 
              he could adapt naturally.</strong> By interview day, he'd practiced his stories so many times that answering 
              questions felt like having a normal conversation. He got three offers.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              How to Prepare Like a Pro
            </h3>

            <div className="space-y-6 mb-8 text-lg text-gray-700">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Research the company deeply</h4>
                <p>Go beyond the "About Us" page. Read recent news, check their LinkedIn, understand their challenges. 
                Reference specific details in your answers to show you've done your homework.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Prepare STAR stories for common questions</h4>
                <p>For behavioral questions ("Tell me about a time when..."), use the STAR method: Situation, Task, 
                Action, Result. Prepare 5-7 stories that showcase different skills. Practice telling them conversationally.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Craft your "Tell me about yourself" answer</h4>
                <p>This isn't your life story. It's a 60-90 second pitch covering: your current role, relevant experience, 
                and why you're excited about this opportunity. Make it compelling and conversational.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Practice out loud, not just in your head</h4>
                <p>Record yourself answering questions. It feels awkward, but you'll catch filler words, rambling, 
                and unclear phrasing. Your brain tricks you into thinking you sound better than you do — video doesn't lie.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Prepare smart questions to ask them</h4>
                <p>Interviews end with "Do you have questions for us?" This is your chance to show engagement and 
                evaluate fit. Prepare 3-5 thoughtful questions about the role, team culture, or company direction.</p>
              </div>
            </div>

            <p className="text-lg leading-relaxed text-gray-700 mb-8 bg-brand-gold/5 p-6 rounded-lg border-l-4 border-brand-gold">
              💡 <strong>Pro tip:</strong> Create a "brag document" before interview prep. List your achievements, 
              metrics, projects, and skills. When you're nervous and drawing blanks, having concrete examples written 
              down helps jog your memory.
            </p>

            {/* Section 3 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Mastering the Opening Minutes
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              The first 5 minutes of an interview set the tone for everything that follows. Research shows interviewers 
              often form initial impressions within 30 seconds — and spend the rest of the interview confirming that bias.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              <strong>Start strong, and you've already won half the battle.</strong>
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Opening Strategies That Work
            </h3>

            <ul className="space-y-4 mb-8 text-lg text-gray-700">
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Make strong eye contact and smile.</strong> It signals confidence and warmth. Even in 
                video interviews, look at the camera when you're speaking, not at their face on screen.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Use a firm handshake (in-person) or a warm greeting (virtual).</strong> Your first words 
                matter. "It's great to meet you, thank you for taking the time" beats "Hi, I'm nervous."</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Sit with good posture.</strong> Lean slightly forward to show engagement. Avoid slouching 
                or crossing your arms — it reads as defensive.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Match their energy level.</strong> If they're formal and professional, mirror that. If 
                they're casual and friendly, you can relax a bit. This builds rapport quickly.</span>
              </li>
            </ul>

            {/* Section 4 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Answering Questions Confidently (Even When You Don't Know the Answer)
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Here's the secret hiring managers won't tell you: <strong>they don't expect you to have perfect answers. 
              They want to see how you think under pressure.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I once interviewed a candidate who didn't know the answer to a technical question. Instead of panicking 
              or making something up, he said: "That's not something I've worked with directly, but here's how I'd 
              approach learning it..." Then he outlined his problem-solving process. I hired him. His honesty and 
              resourcefulness were more valuable than rote knowledge.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Techniques for Strong Answers
            </h3>

            <div className="bg-brand-dark-blue/5 p-6 rounded-lg mb-8">
              <ul className="space-y-3 text-lg text-gray-700">
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">1.</span>
                  <span><strong>Pause before answering.</strong> Don't rush to fill silence. Take 2-3 seconds to 
                  gather your thoughts. It makes you appear thoughtful, not unprepared.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">2.</span>
                  <span><strong>Structure your answers.</strong> Use frameworks like STAR or "First... Second... Finally..." 
                  Structure prevents rambling and keeps answers concise.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">3.</span>
                  <span><strong>Be specific, not vague.</strong> Don't say "I'm a great team player." Say "On my last 
                  project, I coordinated with 5 departments to launch the feature 2 weeks early." Details matter.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">4.</span>
                  <span><strong>Connect your answers to the job.</strong> Always link back to how your experience or 
                  skills apply to this specific role. Show them you're already thinking like their employee.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-brand-dark-blue font-bold">5.</span>
                  <span><strong>If you don't know, be honest.</strong> Say "I'm not familiar with that specifically, 
                  but here's my relevant experience..." or "That's something I'd love to learn more about."</span>
                </li>
              </ul>
            </div>

            {/* Section 5 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Managing Interview Anxiety in Real-Time
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Even with perfect preparation, you might feel your heart racing during the interview. That's normal. 
              What separates confident interviewees from nervous ones isn't the absence of anxiety — it's <strong>how 
              they manage it.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              I had a client who would physically shake during interviews. We worked on a simple routine: before entering 
              the building, she'd do power poses for 2 minutes (hands on hips, standing tall). Then she'd do box breathing 
              (4 counts in, 4 hold, 4 out, 4 hold) for 3 cycles. By the time she walked in, her nervous system had calmed 
              down enough that she could focus on the conversation.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Anxiety Management Techniques
            </h3>

            <ul className="space-y-4 mb-8 text-lg text-gray-700">
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Reframe nervousness as excitement.</strong> Both create the same physical sensation 
                (increased heart rate, adrenaline). Tell yourself "I'm excited" instead of "I'm nervous."</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Use controlled breathing.</strong> Before the interview and during breaks in conversation, 
                breathe deeply from your diaphragm. It activates your parasympathetic nervous system.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Ground yourself physically.</strong> Feel your feet on the floor. Press your palms together 
                briefly under the table. Physical grounding reduces mental spiraling.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Have a sip of water when you need a moment.</strong> It's perfectly acceptable to pause 
                and take a drink. It gives you a second to collect your thoughts.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-gold font-bold">•</span>
                <span><strong>Remember: they're rooting for you.</strong> The interviewer wants to fill this role. 
                They're hoping you're the answer. They're on your side.</span>
              </li>
            </ul>

            {/* Section 6 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Your Voice and Body Language in Interviews
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              What you say matters. But <strong>how you say it matters just as much.</strong>
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              Your voice carries emotion. Enthusiasm. Confidence. Or uncertainty and doubt. I've seen qualified candidates 
              lose offers because they spoke in a monotone or ended every sentence like a question. Your vocal delivery 
              shapes how interviewers perceive your competence.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Vocal and Non-Verbal Tips
            </h3>

            <div className="space-y-6 mb-8 text-lg text-gray-700">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Speak at a moderate pace</h4>
                <p>When nervous, we tend to speed up. Consciously slow down slightly. Pause between thoughts. 
                It makes you sound thoughtful and in control.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Project your voice with confidence</h4>
                <p>Don't shout, but speak loudly enough to be heard clearly. A strong voice signals authority. 
                A weak, quiet voice makes people doubt your conviction.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Avoid upspeak (rising inflection at end of sentences)</h4>
                <p>Statements shouldn't sound like questions. Make declarative sentences end on a downward note. 
                Upspeak makes you sound uncertain about your own answers.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Use hand gestures naturally</h4>
                <p>Don't keep your hands glued to your sides or hidden under the table. Natural gestures show 
                engagement and help you think. Just don't overdo it — you're not conducting an orchestra.</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">Make consistent (not constant) eye contact</h4>
                <p>Hold eye contact when making important points, then naturally break away. If there are multiple 
                interviewers, distribute your attention evenly among them.</p>
              </div>
            </div>

            {/* Section 7 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Handling Difficult or Unexpected Questions
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              "What's your biggest weakness?" "Why should we hire you over other candidates?" "Where do you see yourself 
              in 5 years?" These questions make even experienced professionals squirm.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              The trick isn't having a perfect answer memorized. It's <strong>staying calm and responding thoughtfully 
              rather than reactively.</strong>
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              Common Tough Questions and How to Handle Them
            </h3>

            <div className="space-y-6 mb-8 text-lg text-gray-700">
              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">"Tell me about a time you failed."</h4>
                <p>Choose a real failure (not a humble-brag), explain what you learned, and how you applied that 
                lesson successfully afterward. They want to see self-awareness and growth.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">"What's your biggest weakness?"</h4>
                <p>Pick a real weakness that won't disqualify you, and explain what you're doing to improve it. 
                "I used to struggle with delegating, so I've been working with my team to trust them with more ownership."</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">"Why did you leave your last job?"</h4>
                <p>Never badmouth previous employers. Focus on what you're moving toward (growth, new challenges, 
                alignment with values) rather than what you're running from.</p>
              </div>

              <div className="border-l-4 border-brand-gold pl-6 py-2">
                <h4 className="font-bold text-gray-900 mb-2">"Why is there a gap in your employment?"</h4>
                <p>Be honest and brief. Whether it was family, health, or layoffs — explain what you did during 
                that time to stay current (learning, volunteering, freelancing). Then pivot to why you're ready now.</p>
              </div>
            </div>

            {/* Section 8 */}
            <h2 className="text-3xl font-bold text-brand-dark-blue mt-12 mb-6">
              Closing Strong and Following Up
            </h2>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              The interview isn't over when they ask "Any final questions?" How you close matters.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              End with enthusiasm and clarity. Express genuine interest: "I'm really excited about this opportunity. 
              Based on everything we've discussed, I'm confident I can contribute to [specific goal]. What are the 
              next steps in your process?"
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-8">
              Then, within 24 hours, send a thank-you email. Not generic — reference specific moments from your 
              conversation. "I appreciated your insight about X. It reinforced my excitement about Y." It keeps you 
              top of mind and shows professionalism.
            </p>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-brand-dark-blue to-brand-gold p-8 rounded-2xl text-white my-12">
              <h3 className="text-2xl font-bold mb-4">
                Practice Your Interview Answers
              </h3>
              <p className="text-lg mb-6 text-white/90">
                Use Pavone to record yourself answering common interview questions. Get AI-powered feedback on your 
                delivery, pace, clarity, and confidence. See exactly how you come across — and improve before the real interview.
              </p>
              <TryForFreeButton size="md" />
            </div>

            {/* FAQ Section */}
            <div className="mt-16 mb-12">
              <h2 className="text-3xl font-bold text-brand-dark-blue mb-8">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How can I stop being so nervous in job interviews?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Interview anxiety is normal, but you can manage it. Practice your answers out loud beforehand, 
                    use deep breathing before and during the interview, and reframe nervousness as excitement. The more 
                    interviews you do, the more comfortable you'll become. Preparation is the best anxiety reducer.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    What should I do if I blank on an interview question?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Take a pause, breathe, and ask for clarification: "That's a great question. To make sure I answer 
                    it fully, are you asking about X or Y?" This buys you time to think and shows you want to give a 
                    thoughtful answer. If you still don't know, be honest: "I'd need to think more about that, but here's 
                    my initial approach..."
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How long should my answers be in an interview?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Aim for 1-2 minutes per answer for most questions. Longer for complex behavioral questions with stories. 
                    If you see their attention drifting, wrap up. Watch for cues — if they're taking notes, they're engaged. 
                    If they're looking at their watch, be more concise.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Should I admit when I don't know something in an interview?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Yes. Honesty beats faking knowledge. Say "I haven't worked with that directly, but here's my related 
                    experience" or "That's not my area of expertise, but I'm a fast learner — here's how I'd approach it." 
                    Employers value integrity and problem-solving over knowing everything.
                  </p>
                </div>

                <div className="border-l-4 border-brand-gold pl-6 py-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    How can I practice for interviews effectively?
                  </h3>
                  <p className="text-lg text-gray-700">
                    Record yourself answering common interview questions on video. Watch it back critically — you'll catch 
                    filler words, nervous habits, and unclear answers. Do mock interviews with friends or use AI tools 
                    for feedback. The key is practicing out loud, not just reading notes. Repetition builds confidence.
                  </p>
                </div>
              </div>
            </div>

            {/* Long-tail Keywords Section (hidden from users, for SEO) */}
            <div className="sr-only">
              <h2>Related Topics and Keywords</h2>
              <ul>
                <li>how to answer interview questions confidently</li>
                <li>job interview preparation tips</li>
                <li>reduce interview anxiety</li>
                <li>speaking confidently in job interviews</li>
                <li>how to prepare for a job interview</li>
                <li>answering tough interview questions</li>
                <li>interview communication skills</li>
                <li>what to say in a job interview</li>
                <li>how to impress hiring managers</li>
                <li>job interview confidence tips</li>
                <li>behavioral interview answers</li>
                <li>STAR method interview questions</li>
              </ul>
            </div>
          </motion.div>

          {/* Author/Date Footer */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900">Pavone Academy</p>
                <p>Expert communication coaching and career tips</p>
              </div>
              <Link
                to="/academy"
                className="text-brand-dark-blue hover:text-brand-gold font-medium transition-colors"
              >
                Read More Articles →
              </Link>
            </div>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-[1080px] mx-auto px-4 flex flex-col items-center gap-3 md:flex-row md:justify-between">
          <p className="text-sm text-muted-foreground">
            © 2026 Pavone. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <a href="/privacy" className="hover:underline">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:underline">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

